import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Ticket, X } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import { useModuleStore } from '@shared/stores/moduleStore';
import {
  fetchAccessInvites,
  fetchAccessEntryTypesForInvite,
  cancelAccessInvite,
  type AccessInvite,
} from '@features/access-invites/owner/api';
import { NewAccessInviteSheet } from '@features/access-invites/owner/NewAccessInvite';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

/** Statuses that mean the invite is no longer live (mirrors AccessInvitesList). */
const DEAD_STATUSES = new Set([
  'cancelled',
  'canceled',
  'expired',
  'revoked',
  'declined',
  'rejected',
]);

function isActive(invite: AccessInvite): boolean {
  return !DEAD_STATUSES.has((invite.status ?? '').toLowerCase());
}

function isCancellable(invite: AccessInvite): boolean {
  const status = (invite.status ?? '').toLowerCase();
  return isActive(invite) && status !== 'accepted' && !invite.accepted_at;
}

function statusLabel(invite: AccessInvite): string {
  const status = (invite.status ?? '').toLowerCase();
  if (status === 'accepted' || invite.accepted_at) return 'Accepted';
  if (!status || status === 'draft' || status === 'pending') return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Home widget: access management. Shows the member's active outgoing visitor
 * invites (with cancel) and a horizontally scrollable rail of the access/entry
 * types available to them — tapping a type opens the new-invite sheet preselected
 * to it. Hidden entirely when the access-management module isn't licensed.
 */
export function AccessManagementWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accessEnabled = useModuleStore((s) => s.isModuleEnabled('access_management'));

  const [newOpen, setNewOpen] = useState(false);
  const [pendingEntryTypeId, setPendingEntryTypeId] = useState<string | undefined>();

  const invitesQuery = useQuery({
    queryKey: ['access-invites'],
    queryFn: fetchAccessInvites,
    enabled: accessEnabled,
  });
  const typesQuery = useQuery({
    queryKey: ['access-entry-types'],
    queryFn: fetchAccessEntryTypesForInvite,
    enabled: accessEnabled,
  });

  const cancelMutation = useMutation({
    mutationFn: cancelAccessInvite,
    onSuccess: () => {
      toast.success('Invite cancelled');
      void queryClient.invalidateQueries({ queryKey: ['access-invites'] });
    },
    onError: () => toast.danger("Couldn't cancel invite"),
  });

  if (!accessEnabled) return null;

  const activeInvites = (invitesQuery.data ?? []).filter(isActive);
  const entryTypes = typesQuery.data ?? [];

  const openNewInvite = (entryTypeId?: string) => {
    setPendingEntryTypeId(entryTypeId);
    setNewOpen(true);
  };

  return (
    <>
      <WidgetCard
        title={widgetTitle(config)}
        actionLabel="Manage"
        onAction={() => navigate('/app/access-invites')}
      >
        {/* Active outgoing invites */}
        {invitesQuery.isLoading ? (
          <Skeleton className="h-12" />
        ) : activeInvites.length > 0 ? (
          <div className="mb-3 flex flex-col gap-2">
            {activeInvites.map((invite) => (
              <div
                key={invite.access_invite_id}
                className="flex items-center gap-3 rounded-xl bg-sunken px-3 py-2.5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                  <Ticket className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="break-words text-body-sm font-semibold text-ink">
                    {invite.visitor_name?.trim() || 'Visitor'}
                  </p>
                  <p className="break-words text-caption text-ink-secondary">
                    {statusLabel(invite)}
                    {invite.reference ? ` · ${invite.reference}` : ''}
                  </p>
                </div>
                {isCancellable(invite) ? (
                  <button
                    type="button"
                    onClick={() => cancelMutation.mutate(invite.access_invite_id)}
                    disabled={cancelMutation.isPending}
                    className="flex shrink-0 items-center gap-1 rounded-pill bg-card px-2.5 py-1 text-caption font-semibold text-danger hairline disabled:opacity-60"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                    Cancel
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-3 rounded-lg bg-sunken px-4 py-3 text-center text-caption text-ink-secondary">
            No active visitor invites.
          </p>
        )}

        {/* Entry types available to me — horizontally scrollable */}
        <p className="mb-2 px-0.5 text-caption font-semibold text-ink-secondary">
          Invite a visitor
        </p>
        {typesQuery.isLoading ? (
          <div className="flex gap-2.5">
            <Skeleton className="h-20 w-28 shrink-0 rounded-2xl" />
            <Skeleton className="h-20 w-28 shrink-0 rounded-2xl" />
          </div>
        ) : entryTypes.length === 0 ? (
          <p className="rounded-lg bg-sunken px-4 py-3 text-center text-caption text-ink-secondary">
            No access types available to you.
          </p>
        ) : (
          <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {entryTypes.map((type) => (
              <button
                key={type.access_entry_type_id}
                type="button"
                onClick={() => openNewInvite(type.access_entry_type_id)}
                className="flex h-20 w-28 shrink-0 flex-col rounded-2xl border border-hairline-strong bg-elevated p-3 text-left transition-transform active:scale-[0.98]"
              >
                <span className="mb-2 flex h-7 w-7 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                  <DoorOpen className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <span className="block break-words text-body-sm font-semibold leading-snug text-ink line-clamp-2">
                  {type.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </WidgetCard>

      <NewAccessInviteSheet
        open={newOpen}
        onOpenChange={setNewOpen}
        initialEntryTypeId={pendingEntryTypeId}
        onCreated={() => {
          setNewOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['access-invites'] });
        }}
      />
    </>
  );
}
